import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, List, MapPin, X, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
const EVENTS = [{
  id: 1,
  title: '30-Day Fitness Challenge',
  date: 'Jan 15, 2025',
  monthDay: 'JAN 15',
  location: 'Virtual',
  price: 'Free',
  type: 'Fitness Challenge',
  typeColor: 'bg-fuchsia text-white',
  description: 'Transform your body and mindset in 30 days with daily workouts, nutrition tips, and community accountability.',
  image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?q=80&w=2070&auto=format&fit=crop'
}, {
  id: 2,
  title: 'Meal Prep Workshop',
  date: 'Feb 8, 2025',
  monthDay: 'FEB 08',
  location: 'Cape Town',
  price: 'R250',
  type: 'Workshop',
  typeColor: 'bg-gold text-charcoal',
  description: 'Learn to prep a full week of healthy meals in under 2 hours. Hands-on, practical, delicious.',
  image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?q=80&w=2053&auto=format&fit=crop'
}, {
  id: 3,
  title: 'Women in Business Networking Brunch',
  date: 'Feb 22, 2025',
  monthDay: 'FEB 22',
  location: 'Johannesburg',
  price: 'R350',
  type: 'Networking',
  typeColor: 'bg-plum text-white',
  description: 'Connect with like-minded women over brunch. Keynote speaker, speed networking, and real conversations.',
  image: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?q=80&w=2070&auto=format&fit=crop'
}, {
  id: 4,
  title: 'Executive Wellness Retreat',
  date: 'Mar 15-17, 2025',
  monthDay: 'MAR 15',
  location: 'Drakensberg',
  price: 'R4,500',
  type: 'Coaching Intensive',
  typeColor: 'bg-charcoal text-white',
  description: 'Three days of fitness, mindfulness, strategic planning, and deep rest. Limited to 20 women.',
  image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=2120&auto=format&fit=crop'
}, {
  id: 5,
  title: 'Digital Declutter Workshop',
  date: 'Apr 5, 2025',
  monthDay: 'APR 05',
  location: 'Virtual',
  price: 'R150',
  type: 'Workshop',
  typeColor: 'bg-gold text-charcoal',
  description: 'Clean up your digital life — inbox zero, file organisation, and productivity systems that stick.',
  image: 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?q=80&w=2070&auto=format&fit=crop'
}, {
  id: 6,
  title: 'Clarity Community Meetup',
  date: 'Apr 19, 2025',
  monthDay: 'APR 19',
  location: 'Durban',
  price: 'Free',
  type: 'Networking',
  typeColor: 'bg-plum text-white',
  description: 'Monthly community gathering. Share wins, get support, and build your tribe.',
  image: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?q=80&w=2070&auto=format&fit=crop'
}];
const PAST_EVENTS = [{
  title: 'End of Year Gala 2024',
  image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=2069&auto=format&fit=crop'
}, {
  title: 'Spring Wellness Retreat',
  image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1999&auto=format&fit=crop'
}, {
  title: 'Founders Breakfast',
  image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32d7?q=80&w=1932&auto=format&fit=crop'
}, {
  title: 'Summer Fitness Challenge',
  image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=2070&auto=format&fit=crop'
}];
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function Events() {
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [selectedEvent, setSelectedEvent] = useState<(typeof EVENTS)[0] | null>(null);
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleRegister = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const honeypot = formData.get('bot-field');
    if (honeypot) return; // Spam detected
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const phone = formData.get('phone') as string;
    const newErrors: Record<string, string> = {};
    if (!name) newErrors.name = 'Name is required';
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) newErrors.email = 'Valid email is required';
    if (!phone) newErrors.phone = 'Phone number is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setFormState('loading');
    setTimeout(() => {
      setFormState('success');
    }, 1500);
  };
  const closeRegistration = () => {
    setSelectedEvent(null);
    setTimeout(() => setFormState('idle'), 300);
  };
  // Simple calendar generation for current month (placeholder logic)
  const daysInMonth = Array.from({
    length: 31
  }, (_, i) => i + 1);
  const eventDays = [8, 15, 22]; // Placeholder days with events
  return <>
      <main className="flex flex-col min-h-screen bg-warmwhite pt-20" data-id="element-500" data-mp="3eq3ud1gr5g0r">
        {/* HERO SECTION */}
        <section className="bg-plum py-24 relative overflow-hidden" data-id="element-501" data-mp="3tn3713qgnz23">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-fuchsia/20 via-transparent to-transparent" data-id="element-502" data-mp="3oorxku42vqke" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-503" data-mp="2a8h8rvjm1ba">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="font-display text-4xl md:text-6xl font-bold text-white mb-6" data-id="element-504" data-mp="2cmen7dp3pm0">
              Experiences that move you forward.
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-lg md:text-xl text-warmwhite/80 max-w-2xl mx-auto font-body" data-id="element-505" data-mp="3e2t5zwi1cyw2">
              Workshops, challenges, and gatherings designed to connect,
              inspire, and transform.
            </motion.p>
          </div>
        </section>

        {/* EVENTS SECTION */}
        <section className="py-24" data-id="element-506" data-mp="22hw3x5v034ut">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-507" data-mp="27ew9lfnxu5le">
            <div className="flex justify-between items-center mb-12" data-id="element-508" data-mp="27fec2u171g1r">
              <h2 className="font-display text-3xl font-bold text-plum" data-id="element-509" data-mp="1zwewz0krmao7">
                Upcoming Events
              </h2>
              <div className="flex bg-white rounded-full p-1 shadow-sm border border-charcoal/5" data-id="element-510" data-mp="1v3lsmdt7l4cn">
                <button onClick={() => setView('list')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${view === 'list' ? 'bg-plum text-white' : 'text-charcoal/60 hover:text-plum'}`} data-id="element-511" data-mp="1q63feb85s2yu">
                  <List className="w-4 h-4" data-id="element-512" data-mp="2dvxr2pgqrlhi" />
                  <span className="hidden sm:inline" data-id="element-513" data-mp="28w1mop44ts1f">List View</span>
                </button>
                <button onClick={() => setView('calendar')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${view === 'calendar' ? 'bg-plum text-white' : 'text-charcoal/60 hover:text-plum'}`} data-id="element-514" data-mp="24uq61938kh5d">
                  <CalendarDays className="w-4 h-4" data-id="element-515" data-mp="2skb2liewkz51" />
                  <span className="hidden sm:inline" data-id="element-516" data-mp="2sirjlk1v49zk">Calendar View</span>
                </button>
              </div>
            </div>

            {view === 'calendar' ? <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="bg-white rounded-3xl p-8 shadow-sm border border-charcoal/5" data-id="element-517" data-mp="2jkek5yj3wwif">
                <div className="flex justify-between items-center mb-8" data-id="element-518" data-mp="3g9nggvl6dvdo">
                  <h3 className="font-display text-2xl font-bold text-charcoal" data-id="element-519" data-mp="3gbq8fl5w1yl1">
                    February 2025
                  </h3>
                  <div className="flex gap-2" data-id="element-520" data-mp="37gsc043j83oy">
                    <button className="p-2 rounded-full hover:bg-warmwhite transition-colors" data-id="element-521" data-mp="37fh7g229irws">
                      <ChevronLeft className="w-5 h-5 text-charcoal" data-id="element-522" data-mp="3v5uzhskukqrd" />
                    </button>
                    <button className="p-2 rounded-full hover:bg-warmwhite transition-colors" data-id="element-523" data-mp="3ttg2gkz7gi6">
                      <ChevronRight className="w-5 h-5 text-charcoal" data-id="element-524" data-mp="d5hekw82dat3" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-4 text-center mb-4" data-id="element-525" data-mp="mxn31vz3xa3j">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-sm font-medium text-charcoal/50 uppercase tracking-wider" data-id="element-526" data-mp="3qdu7w5t9dddw">
                        {day}
                      </div>)}
                </div>
                <div className="grid grid-cols-7 gap-4" data-id="element-527" data-mp="3vcx3z0r9g99q">
                  {/* Empty days offset */}
                  {Array.from({
                length: 5
              }).map((_, i) => <div key={`empty-${i}`} className="aspect-square" data-id="element-528" data-mp="1amy6xmbr7klw" data-mpi={i} />)}
                  {daysInMonth.map(day => {
                const hasEvent = eventDays.includes(day);
                return <div key={day} className={`aspect-square flex flex-col items-center justify-center rounded-xl border transition-colors ${hasEvent ? 'border-fuchsia/30 bg-fuchsia/5 cursor-pointer hover:bg-fuchsia/10' : 'border-transparent hover:border-charcoal/10'}`} data-id="element-529" data-mp="18o27ehih1h18">
                        <span className={`text-lg ${hasEvent ? 'font-bold text-plum' : 'text-charcoal'}`} data-id="element-530" data-mp="ut9kuz45z0l5">
                          {day}
                        </span>
                        {hasEvent && <div className="w-1.5 h-1.5 rounded-full bg-fuchsia mt-1" data-id="element-531" data-mp="fzn3or4sil57" />}
                      </div>;
              })}
                </div>
                <div className="mt-8 pt-8 border-t border-charcoal/10" data-id="element-532" data-mp="2ado2xd495a5t">
                  <p className="text-charcoal/60 text-center" data-id="element-533" data-mp="2iu6lqwxeh19j">
                    Select a highlighted date to view events.
                  </p>
                </div>
              </motion.div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-534" data-mp="2rmqbt8zmlxir">
                {EVENTS.map((event, index) => <motion.div key={event.id} initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={{
              hidden: {
                opacity: 0,
                y: 30
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  delay: index * 0.1,
                  duration: 0.5
                }
              }
            }} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-charcoal/5 group hover:-translate-y-1 hover:shadow-md transition-all duration-300 flex flex-col" data-id="element-535" data-mp="2wmw8j3kr6a7p" data-mpi={index}>
                    <div className="relative h-48 overflow-hidden" data-id="element-536" data-mp="34ko8dulp6j1n">
                      <img src={event.image} alt={event.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-537" data-mp="34ibt5p7bqmsa" />
                      <div className="absolute top-4 left-4 bg-plum text-white text-xs font-bold px-3 py-1.5 rounded-full tracking-wider" data-id="element-538" data-mp="2qlorxa8c9agk">
                        {event.monthDay}
                      </div>
                      <div className={`absolute top-4 right-4 text-xs font-bold px-3 py-1.5 rounded-full ${event.typeColor}`} data-id="element-539" data-mp="1y31bw9878lmc">
                        {event.type}
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1" data-id="element-540" data-mp="ddnkifrhz251">
                      <h3 className="font-display text-xl font-bold text-charcoal mb-2 line-clamp-2" data-id="element-541" data-mp="dg08panum8qj">
                        {event.title}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-charcoal/60 mb-4" data-id="element-542" data-mp="3ovuwe9wstxa">
                        <MapPin className="w-4 h-4" data-id="element-543" data-mp="req7cuqbggh0" />
                        <span data-id="element-544" data-mp="wcroha46m5pu">{event.location}</span>
                        <span className="mx-1" data-id="element-545" data-mp="wbgnfi24fc2f">•</span>
                        <span className={event.price === 'Free' ? 'text-green-600 font-medium' : 'text-fuchsia font-medium'} data-id="element-546" data-mp="ida73ft7tuqi">
                          {event.price}
                        </span>
                      </div>
                      <p className="text-charcoal/70 text-sm mb-6 line-clamp-2 flex-1" data-id="element-547" data-mp="3nbz1xrfiuq3k">
                        {event.description}
                      </p>
                      <button onClick={() => setSelectedEvent(event)} className="w-full py-3 bg-fuchsia text-white rounded-full font-medium hover:bg-fuchsia-light transition-colors" data-id="element-548" data-mp="3ijo4cv897ge6">
                        Register Now
                      </button>
                    </div>
                  </motion.div>)}
              </div>}
          </div>
        </section>

        {/* PAST EVENTS GALLERY */}
        <section className="py-24 bg-charcoal text-white overflow-hidden" data-id="element-549" data-mp="5yh00s0pw4t">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-id="element-550" data-mp="3sbtt7w37lhp5">
            <h2 className="font-display text-3xl font-bold" data-id="element-551" data-mp="2kx8mhsbx0gq3">Past Events</h2>
          </div>
          <div className="flex overflow-x-auto pb-8 px-4 sm:px-6 lg:px-8 gap-6 snap-x hide-scrollbar" data-id="element-552" data-mp="2gvwslam0d9c3">
            {PAST_EVENTS.map((event, index) => <div key={index} className="relative w-80 md:w-96 h-64 flex-shrink-0 rounded-2xl overflow-hidden group snap-center cursor-pointer" data-id="element-553" data-mp="wf5udlx46iii" data-mpi={index}>
                <img src={event.image} alt={event.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" data-id="element-554" data-mp="15dr7v2nqb7cf" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-300" data-id="element-555" data-mp="1f6genk8c3x1f" />
                <div className="absolute bottom-6 left-6 right-6" data-id="element-556" data-mp="1o5tr8lrr91qd">
                  <h3 className="font-display text-xl font-bold text-white translate-y-2 group-hover:translate-y-0 transition-transform duration-300" data-id="element-557" data-mp="1t4dc0ium47h5">
                    {event.title}
                  </h3>
                </div>
              </div>)}
          </div>
        </section>

        {/* REGISTRATION MODAL */}
        <AnimatePresence data-id="element-558" data-mp="1219tibjqgcjo">
          {selectedEvent && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/80 backdrop-blur-sm" data-id="element-559" data-mp="x2os48sqvmtn">
              <motion.div initial={{
            scale: 0.95,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} exit={{
            scale: 0.95,
            opacity: 0
          }} className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl relative max-h-[90vh] overflow-y-auto" data-id="element-560" data-mp="1kxj42ovfekyx">
                <button onClick={closeRegistration} className="absolute top-4 right-4 p-2 bg-white/50 hover:bg-white rounded-full text-charcoal transition-colors z-10" data-id="element-561" data-mp="1yp60qpta5vi8">
                  <X className="w-5 h-5" data-id="element-562" data-mp="28ewgn31hvk4o" />
                </button>

                <div className="relative h-32 bg-plum" data-id="element-563" data-mp="28emr1gkd3ezi">
                  <img src={selectedEvent.image} alt={selectedEvent.title} className="w-full h-full object-cover opacity-50" data-id="element-564" data-mp="2dd6ejlelsytz" />
                  <div className="absolute inset-0 flex items-center px-8" data-id="element-565" data-mp="24dj98oir78vn">
                    <h3 className="font-display text-2xl font-bold text-white line-clamp-2" data-id="element-566" data-mp="1pon7183doq2y">
                      {selectedEvent.title}
                    </h3>
                  </div>
                </div>

                <div className="p-8" data-id="element-567" data-mp="1gn6vgx0tuhx5">
                  {formState === 'success' ? <div className="text-center py-8" data-id="element-568" data-mp="1kp27avgdwdp6">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-569" data-mp="1kosbgr8nrcbk">
                        <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-570" data-mp="212grr1nc6zz1" />
                      </div>
                      <h4 className="font-display text-2xl font-bold text-charcoal mb-2" data-id="element-571" data-mp="1w3vrj2834gal">
                        You're registered!
                      </h4>
                      <p className="text-charcoal/70 mb-8" data-id="element-572" data-mp="1m6r0op3anwng">
                        We've sent the details to your email. We can't wait to
                        see you there.
                      </p>
                      <button onClick={closeRegistration} className="px-8 py-3 bg-plum text-white rounded-full font-medium hover:bg-plum-light transition-colors" data-id="element-573" data-mp="1ddnriszom5ym">
                        Close
                      </button>
                    </div> : <form onSubmit={handleRegister} className="space-y-5" data-id="element-574" data-mp="ugcqb2uhp2gj">
                      <div className="bg-warmwhite p-4 rounded-xl mb-6" data-id="element-575" data-mp="uih4qeq0ljv2">
                        <p className="text-sm text-charcoal/70 mb-1" data-id="element-576" data-mp="362y8f5t9awum">
                          Registering for:
                        </p>
                        <p className="font-medium text-charcoal" data-id="element-577" data-mp="3f4ehna8c4sx9">
                          {selectedEvent.title} • {selectedEvent.date}
                        </p>
                      </div>

                      {/* Honeypot */}
                      <input type="text" name="bot-field" className="hidden" tabIndex={-1} data-id="element-578" data-mp="2mgicdh4p9sov" />

                      <div data-id="element-579" data-mp="2wcvdh02kr8tn">
                        <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-1" data-id="element-580" data-mp="2wclo9epct5p0">
                          Full Name
                        </label>
                        <input type="text" id="name" name="name" className={`w-full px-4 py-3 rounded-xl border ${errors.name ? 'border-red-500 focus:ring-red-500' : 'border-charcoal/20 focus:border-fuchsia focus:ring-fuchsia'} focus:outline-none focus:ring-1 transition-colors bg-white`} aria-describedby={errors.name ? 'name-error' : undefined} data-id="element-581" data-mp="28nslayabdxkw" />
                        {errors.name && <p id="name-error" className="mt-1 text-sm text-red-500" data-id="element-582" data-mp="12tow4q88f2vf">
                            {errors.name}
                          </p>}
                      </div>

                      <div data-id="element-583" data-mp="f5fgk61m3bcq">
                        <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-1" data-id="element-584" data-mp="f44d67c2w5m5">
                          Email Address
                        </label>
                        <input type="email" id="email" name="email" className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-500 focus:ring-red-500' : 'border-charcoal/20 focus:border-fuchsia focus:ring-fuchsia'} focus:outline-none focus:ring-1 transition-colors bg-white`} aria-describedby={errors.email ? 'email-error' : undefined} data-id="element-585" data-mp="2tconzunj03sl" />
                        {errors.email && <p id="email-error" className="mt-1 text-sm text-red-500" data-id="element-586" data-mp="20oj57dg18ove">
                            {errors.email}
                          </p>}
                      </div>

                      <div data-id="element-587" data-mp="1h2h7jupgli74">
                        <label htmlFor="phone" className="block text-sm font-medium text-charcoal mb-1" data-id="element-588" data-mp="1h164xy7r2ghl">
                          Phone Number
                        </label>
                        <input type="tel" id="phone" name="phone" className={`w-full px-4 py-3 rounded-xl border ${errors.phone ? 'border-red-500 focus:ring-red-500' : 'border-charcoal/20 focus:border-fuchsia focus:ring-fuchsia'} focus:outline-none focus:ring-1 transition-colors bg-white`} aria-describedby={errors.phone ? 'phone-error' : undefined} data-id="element-589" data-mp="183vrc67gmf9i" />
                        {errors.phone && <p id="phone-error" className="mt-1 text-sm text-red-500" data-id="element-590" data-mp="2ni9v704yndyp">
                            {errors.phone}
                          </p>}
                      </div>

                      {selectedEvent.location !== 'Virtual' && <div data-id="element-591" data-mp="lscc7kz6hiiq">
                          <label htmlFor="dietary" className="block text-sm font-medium text-charcoal mb-1" data-id="element-592" data-mp="gu11l81273zg">
                            Dietary Requirements (Optional)
                          </label>
                          <input type="text" id="dietary" name="dietary" className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-fuchsia focus:outline-none focus:ring-1 transition-colors bg-white" data-id="element-593" data-mp="3kcua36ezd2mr" />
                        </div>}

                      <button type="submit" disabled={formState === 'loading'} className="w-full py-4 bg-fuchsia text-white rounded-full font-medium hover:bg-fuchsia-light transition-colors flex justify-center items-center mt-4 disabled:opacity-70" data-id="element-594" data-mp="3f7ig7c68mtx1">
                        {formState === 'loading' ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-595" data-mp="20ytxizkjdkr" /> : `Register for ${selectedEvent.price}`}
                      </button>
                    </form>}
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </main>
    </>;
}